package com.example.hukakmp.ui

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.ui.Modifier
import com.example.hukakmp.model.PartePesca
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event

private const val DEFAULT_REPORTS_LAT = -42.0
private const val DEFAULT_REPORTS_LON = -65.0

private fun validReports(reportes: List<PartePesca>): List<PartePesca> =
    reportes.filter { report ->
        val lat = report.ubicacion?.latitud
        val lon = report.ubicacion?.longitud
        lat != null && lon != null &&
            lat in -90.0..90.0 &&
            lon in -180.0..180.0 &&
            !(lat == 0.0 && lon == 0.0)
    }

private fun popupElement(report: PartePesca): HTMLElement {
    val root = document.createElement("div") as HTMLDivElement
    root.style.setProperty("min-width", "230px")
    root.style.setProperty("max-width", "300px")
    root.style.setProperty("font-family", "Inter, system-ui, sans-serif")
    root.style.setProperty("color", "#082435")

    fun line(
        text: String,
        weight: String = "400",
        size: String = "13px",
        marginTop: String = "0",
    ) {
        val element = document.createElement("div") as HTMLDivElement
        element.textContent = text
        element.style.setProperty("font-weight", weight)
        element.style.setProperty("font-size", size)
        element.style.setProperty("margin-top", marginTop)
        root.appendChild(element)
    }

    line(
        text = report.fecha?.takeIf { it.isNotBlank() } ?: "Sin fecha",
        weight = "700",
        size = "16px",
    )

    val placeAndType = listOfNotNull(
        report.ubicacion?.nombre?.takeIf { it.isNotBlank() },
        report.tipo?.takeIf { it.isNotBlank() }?.replaceFirstChar { it.uppercase() },
    ).joinToString(" · ")

    if (placeAndType.isNotBlank()) {
        line(placeAndType, size = "12px", marginTop = "3px")
    }

    report.fotos
        .firstOrNull { it.startsWith("https://") || it.startsWith("http://") }
        ?.let { photoUrl ->
            val image = document.createElement("img") as HTMLImageElement
            image.src = photoUrl
            image.alt = "Foto del parte"
            image.style.setProperty("width", "100%")
            image.style.setProperty("height", "135px")
            image.style.setProperty("object-fit", "cover")
            image.style.setProperty("border-radius", "10px")
            image.style.setProperty("margin-top", "10px")
            image.style.setProperty("cursor", "pointer")
            image.style.setProperty("background", "#E8F1F5")
            image.asDynamic().loading = "lazy"
            image.onclick = {
                window.open(photoUrl, "_blank")
                Unit
            }
            root.appendChild(image)
        }

    if (report.peces.isNotEmpty()) {
        line("Especies capturadas", weight = "700", size = "12px", marginTop = "10px")
        report.peces.forEach { capture ->
            val returned = if (capture.devueltos > 0) {
                " · " + capture.devueltos + " devueltos"
            } else {
                ""
            }
            line(
                capture.cantidad.toString() + "x " + capture.especie + returned,
                size = "12px",
                marginTop = "3px",
            )
        }
        line(
            report.cantidadTotal.toString() + " peces en total",
            weight = "700",
            size = "12px",
            marginTop = "8px",
        )
    } else {
        line("Sin capturas registradas", size = "12px", marginTop = "10px")
    }

    return root
}

private fun makeOverlayButton(text: String): HTMLButtonElement {
    val button = document.createElement("button") as HTMLButtonElement
    button.textContent = text
    button.style.setProperty("border", "1px solid rgba(255,255,255,0.28)")
    button.style.setProperty("border-radius", "12px")
    button.style.setProperty("padding", "10px 14px")
    button.style.setProperty("background", "#06334A")
    button.style.setProperty("color", "#FFFFFF")
    button.style.setProperty("font-family", "Inter, system-ui, sans-serif")
    button.style.setProperty("font-size", "13px")
    button.style.setProperty("font-weight", "700")
    button.style.setProperty("cursor", "pointer")
    button.style.setProperty("box-shadow", "0 4px 14px rgba(0,0,0,0.24)")
    return button
}

private fun fitReports(map: dynamic, reportes: List<PartePesca>) {
    val leaflet = window.asDynamic().L
    val positioned = validReports(reportes)

    if (positioned.isEmpty()) {
        map.setView(arrayOf(DEFAULT_REPORTS_LAT, DEFAULT_REPORTS_LON), 5)
        return
    }

    if (positioned.size == 1) {
        val only = positioned.first().ubicacion!!
        map.setView(arrayOf(only.latitud!!, only.longitud!!), 13)
        return
    }

    val points = js("[]")
    positioned.forEach { report ->
        points.push(arrayOf(report.ubicacion!!.latitud!!, report.ubicacion!!.longitud!!))
    }

    val options = js("{}")
    options.paddingTopLeft = arrayOf(40, 100)
    options.paddingBottomRight = arrayOf(40, 50)
    options.maxZoom = 13
    map.fitBounds(leaflet.latLngBounds(points), options)
}

private fun addReportMarkers(map: dynamic, reportes: List<PartePesca>) {
    val leaflet = window.asDynamic().L

    validReports(reportes).forEach { report ->
        val lat = report.ubicacion!!.latitud!!
        val lon = report.ubicacion!!.longitud!!

        val options = js("{}")
        options.radius = 9
        options.color = "#E8FAFF"
        options.weight = 3
        options.fillColor = "#0E88C7"
        options.fillOpacity = 0.96

        leaflet
            .circleMarker(arrayOf(lat, lon), options)
            .addTo(map)
            .bindPopup(
                popupElement(report),
                js("({ maxWidth: 320, closeButton: true })"),
            )
    }
}

private fun centerOnCurrentLocation(
    overlay: HTMLDivElement,
    map: dynamic,
    button: HTMLButtonElement,
) {
    val leaflet = window.asDynamic().L
    val geo = window.navigator.asDynamic().geolocation

    if (geo == undefined) {
        button.textContent = "Ubicación no disponible"
        return
    }

    button.textContent = "Buscando..."
    geo.getCurrentPosition(
        { position: dynamic ->
            val lat = (position.coords.latitude as Number).toDouble()
            val lon = (position.coords.longitude as Number).toDouble()

            val oldMarker = overlay.asDynamic().__hukaUserMarker
            if (oldMarker != null && oldMarker != undefined) {
                runCatching { map.removeLayer(oldMarker) }
            }

            val options = js("{}")
            options.radius = 8
            options.color = "#FFFFFF"
            options.weight = 3
            options.fillColor = "#F36F45"
            options.fillOpacity = 1.0

            val marker = leaflet
                .circleMarker(arrayOf(lat, lon), options)
                .addTo(map)
                .bindTooltip("Tu ubicación")

            overlay.asDynamic().__hukaUserMarker = marker
            map.setView(arrayOf(lat, lon), 14)
            button.textContent = "Mi ubicación"
        },
        { _: dynamic ->
            button.textContent = "No se pudo ubicar"
            window.setTimeout({ button.textContent = "Mi ubicación" }, 1800)
        },
    )
}

@Composable
actual fun ReportsOverviewMap(
    reportes: List<PartePesca>,
    onClose: () -> Unit,
    modifier: Modifier,
) {
    DisposableEffect(reportes, onClose) {
        val body = document.body
        if (body == null) {
            return@DisposableEffect onDispose { }
        }

        val overlay = document.createElement("div") as HTMLDivElement
        overlay.style.setProperty("position", "fixed")
        overlay.style.setProperty("inset", "0")
        overlay.style.setProperty("width", "100vw")
        overlay.style.setProperty("height", "100dvh")
        overlay.style.setProperty("z-index", "2147483000")
        overlay.style.setProperty("background", "#E8F1F5")
        overlay.style.setProperty("overflow", "hidden")

        val mapHost = document.createElement("div") as HTMLDivElement
        mapHost.style.setProperty("position", "absolute")
        mapHost.style.setProperty("inset", "0")
        mapHost.style.setProperty("width", "100%")
        mapHost.style.setProperty("height", "100%")
        mapHost.style.setProperty("background", "#E8F1F5")
        overlay.appendChild(mapHost)

        val summary = document.createElement("div") as HTMLDivElement
        summary.style.setProperty("position", "absolute")
        summary.style.setProperty("top", "16px")
        summary.style.setProperty("left", "16px")
        summary.style.setProperty("z-index", "1000")
        summary.style.setProperty("padding", "10px 13px")
        summary.style.setProperty("border-radius", "14px")
        summary.style.setProperty("background", "rgba(3, 27, 41, 0.94)")
        summary.style.setProperty("color", "#FFFFFF")
        summary.style.setProperty("font-family", "Inter, system-ui, sans-serif")
        summary.style.setProperty("box-shadow", "0 5px 18px rgba(0,0,0,0.22)")
        summary.innerHTML =
            "<strong>Mapa de tus partes</strong><br><span style='font-size:11px;color:#C6DCEC'>" +
                validReports(reportes).size + " de " + reportes.size + " jornadas con ubicación</span>"
        overlay.appendChild(summary)

        val actions = document.createElement("div") as HTMLDivElement
        actions.style.setProperty("position", "absolute")
        actions.style.setProperty("top", "16px")
        actions.style.setProperty("right", "16px")
        actions.style.setProperty("z-index", "1001")
        actions.style.setProperty("display", "flex")
        actions.style.setProperty("gap", "8px")

        val locateButton = makeOverlayButton("Mi ubicación")
        val closeButton = makeOverlayButton("Cerrar")
        closeButton.style.setProperty("background", "#0E88C7")
        actions.appendChild(locateButton)
        actions.appendChild(closeButton)
        overlay.appendChild(actions)

        body.appendChild(overlay)

        val leaflet = window.asDynamic().L
        var map: dynamic = null

        if (leaflet == undefined) {
            mapHost.textContent = "No se pudo cargar el mapa."
        } else {
            val mapOptions = js("{}")
            mapOptions.zoomControl = true
            mapOptions.scrollWheelZoom = true

            map = leaflet.map(mapHost, mapOptions)

            val tileOptions = js("{}")
            tileOptions.maxZoom = 19
            tileOptions.attribution = "&copy; OpenStreetMap contributors"

            leaflet
                .tileLayer(
                    "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
                    tileOptions,
                )
                .addTo(map)

            addReportMarkers(map, reportes)

            val refreshMap = {
                map.invalidateSize(false)
                fitReports(map, reportes)
            }

            listOf(0, 60, 180, 400).forEach { delay ->
                window.setTimeout({ refreshMap() }, delay)
            }

            val resizeListener: (Event) -> Unit = {
                window.requestAnimationFrame { _ -> refreshMap() }
            }
            window.asDynamic().addEventListener("resize", resizeListener)
            overlay.asDynamic().__hukaResizeListener = resizeListener

            locateButton.onclick = {
                centerOnCurrentLocation(overlay, map, locateButton)
                Unit
            }
        }

        closeButton.onclick = {
            onClose()
            Unit
        }

        onDispose {
            val resizeListener = overlay.asDynamic().__hukaResizeListener
            if (resizeListener != null && resizeListener != undefined) {
                runCatching {
                    window.asDynamic().removeEventListener("resize", resizeListener)
                }
            }
            if (map != null && map != undefined) {
                runCatching { map.remove() }
            }
            overlay.parentNode?.removeChild(overlay)
        }
    }
}
